import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  PlusIcon,
  SparklesIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
  BeakerIcon,
  FunnelIcon,
  EllipsisHorizontalIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/ui/Button';
import { Slider } from '../components/ui/Slider';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { testCaseService, TestCase } from '../services/testCaseService';
import { userStoryService, UserStory } from '../services/userStoryService';
import { generateTestCasesBulkPDF } from '../utils/testCasePDFGenerator';
import { LoadingOverlay } from '../components/ui/LoadingOverlay';
import toast from 'react-hot-toast';

export const TestCasesPage: React.FC = () => {
  const { id: userStoryId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const isPro = user?.subscribed === 'S' || user?.plan === 'premier' || user?.plan === 'enterprise';

  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [userStory, setUserStory] = useState<UserStory | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [testCaseCount, setTestCaseCount] = useState(5);
  const [extraContext, setExtraContext] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newTestCase, setNewTestCase] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    type: 'Functional',
  });
  const [creatingManual, setCreatingManual] = useState(false);

  useEffect(() => {
    if (userStoryId) {
      loadData();
    }
  }, [userStoryId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tcResponse, usResponse] = await Promise.all([
        testCaseService.getTestCases({ userStoryId }),
        userStoryService.getUserStory(userStoryId!)
      ]);
      setTestCases(tcResponse.data);
      setUserStory(usResponse.data);
    } catch (err: any) {
      toast.error('Failed to load test cases');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenGenerateModal = () => {
    setShowGenerateModal(true);
    setExtraContext('');
  };

  const handleConfirmGenerate = async () => {
    if (!userStoryId) return;
    setShowGenerateModal(false);
    setGenerating(true);
    try {
      await testCaseService.generateTestCases({
        userStoryId,
        regenerate: testCases.length > 0,
        count: testCaseCount,
        extraContext: extraContext || undefined
      });
      toast.success(`Generating ${testCaseCount} test cases...`);
      // Add a slight delay to allow backend processing start or complete short tasks
      await new Promise(resolve => setTimeout(resolve, 1000));
      loadData();
    } catch (err: any) {
      console.error(err);
      toast.error('Failed to generate test cases');
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateTestCase = async () => {
    if (!userStoryId || !newTestCase.title) return;
    setCreatingManual(true);
    try {
      await testCaseService.createTestCase({
        ...newTestCase,
        userStoryId,
        status: 'Draft',
        steps: []
      });
      setIsCreateModalOpen(false);
      setNewTestCase({ title: '', description: '', priority: 'Medium', type: 'Functional' });
      toast.success('Test case created successfully');
      loadData();
    } catch (err: any) {
      toast.error('Failed to create test case');
    } finally {
      setCreatingManual(false);
    }
  };

  const handleDownloadAllPDF = async () => {
    if (testCases.length === 0) {
      toast.error('No test cases to download');
      return;
    }

    setDownloading(true);
    const toastId = toast.loading('Generating PDF...');

    try {
      const title = `Test Cases for ${userStory?.title || 'User Story'}`;
      const blob = await generateTestCasesBulkPDF(testCases, title);

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 16);
      const fileName = `TestCases_${userStoryId || 'US'}_${timestamp}.pdf`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('PDF downloaded successfully', { id: toastId });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF', { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  const filteredTestCases = testCases.filter(tc =>
    tc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tc.tcCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tc.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen relative overflow-hidden bg-[#0B0F19]">
        <div className="absolute inset-0 bg-primary/5 blur-[120px] rounded-full animate-pulse-subtle"></div>
        <ArrowPathIcon className="h-8 w-8 animate-spin text-primary relative z-10" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative overflow-hidden bg-[#0B0F19]">
      <LoadingOverlay 
        isOpen={generating} 
        message="Generating test cases with AI engine..." 
      />
      {/* Background Ambience */}
      <div className="fixed top-[-20%] left-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[150px] rounded-full pointer-events-none transition-all duration-1000"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-[#1E293B]/40 blur-[120px] rounded-full pointer-events-none transition-all duration-1000"></div>

      <div className="max-w-[1400px] mx-auto relative z-10">
        {/* Breadcrumb */}
        <div className="mb-8 flex items-center gap-3 text-[11px] font-black uppercase tracking-widest text-text-secondary/70">
          <button onClick={() => navigate('/dashboard')} className="hover:text-primary transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-primary/10 border border-white/5 hover:border-primary/30">Solutions</button>
          <ChevronLeftIcon className="h-3 w-3 text-text-secondary/30" />
          <button onClick={() => navigate(-2)} className="hover:text-primary transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-primary/10 border border-white/5 hover:border-primary/30">User Stories</button>
          <ChevronLeftIcon className="h-3 w-3 text-text-secondary/30" />
          <span className="text-white px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 shadow-[0_0_15px_rgba(255,255,255,0.05)]">Test Cases</span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="relative flex items-center gap-4">
            <button
                onClick={() => navigate(-1)}
                className="p-1.5 hover:bg-white/10 rounded-lg text-text-secondary hover:text-white transition-colors flex items-center justify-center group"
                title="Back"
            >
                <ChevronLeftIcon className="h-6 w-6 group-hover:-translate-x-0.5 transition-transform" />
            </button>
            <div>
              <div className="absolute -left-6 top-0 bottom-0 w-1 bg-gradient-to-b from-primary to-transparent rounded-r-full"></div>
              <h1 className="text-3xl font-black text-white tracking-tight flex items-center gap-3">
                Test Matrix: {userStory?.title}
              </h1>
              <div className="flex items-center gap-3 mt-3">
                <span className="text-text-secondary text-[11px] font-black uppercase tracking-widest">Validation Sequences</span>
                <span className="bg-primary/10 text-primary border border-primary/30 px-3 py-0.5 rounded-full text-[10px] font-black shadow-[0_0_10px_rgba(255,107,0,0.2)]">
                  {testCases.length} TEST CASES
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              onClick={handleDownloadAllPDF}
              disabled={downloading || testCases.length === 0}
              className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase h-11 px-6 shadow-lg backdrop-blur-md"
            >
              {downloading ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin text-primary" />
                  Compiling...
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2 text-primary" />
                  Export Matrix
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenGenerateModal}
              disabled={generating}
              className="rounded-xl border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 hover:text-white font-black tracking-widest text-[10px] uppercase h-11 px-6 shadow-[0_0_15px_rgba(255,107,0,0.15)] backdrop-blur-md transition-all"
            >
              <SparklesIcon className={`h-4 w-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Regenerating...' : 'Recompute Matrix'}
            </Button>
            <Button
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-primary hover:bg-orange-500 text-white rounded-xl shadow-[0_0_20px_rgba(255,107,0,0.4)] font-black tracking-widest text-[10px] uppercase h-11 px-6 border-none"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Test Case
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="glass-panel rounded-2xl border border-white/5 p-5 mb-8 space-y-4 shadow-xl">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-text-secondary group-focus-within:text-primary transition-colors" />
            </div>
            <input
              type="text"
              placeholder="Search Test Cases..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3.5 bg-black/40 border border-white/10 rounded-xl focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all text-white placeholder-text-secondary/50 font-medium shadow-inner"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="relative">
              <select className="w-full pl-5 pr-10 py-3 bg-black/40 border border-white/10 rounded-xl appearance-none text-[12px] font-black tracking-wider uppercase focus:ring-2 focus:ring-primary/50 transition-all text-white shadow-inner cursor-pointer hover:border-white/20">
                <option value="">PRIORITY VECTOR</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
              <FunnelIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary pointer-events-none" />
            </div>
            <div className="relative">
              <select className="w-full pl-5 pr-10 py-3 bg-black/40 border border-white/10 rounded-xl appearance-none text-[12px] font-black tracking-wider uppercase focus:ring-2 focus:ring-primary/50 transition-all text-white shadow-inner cursor-pointer hover:border-white/20">
                <option value="">CLASS VECTOR</option>
                <option value="Functional">Functional</option>
                <option value="Non-Functional">Non-Functional</option>
                <option value="UI">UI</option>
              </select>
              <FunnelIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary pointer-events-none" />
            </div>
            <div className="relative">
              <select className="w-full pl-5 pr-10 py-3 bg-black/40 border border-white/10 rounded-xl appearance-none text-[12px] font-black tracking-wider uppercase focus:ring-2 focus:ring-primary/50 transition-all text-white shadow-inner cursor-pointer hover:border-white/20">
                <option value="">COMPLEXITY VECTOR</option>
                <option value="1-3">1-3 steps</option>
                <option value="4-6">4-6 steps</option>
                <option value="7+">7+ steps</option>
              </select>
              <FunnelIcon className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-text-secondary pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Test Cases Grid */}
        <div className="grid grid-cols-1 gap-5 overflow-y-auto max-h-[calc(100vh-400px)] pr-2 custom-scrollbar">
          {filteredTestCases.map((tc) => (
            <div
              key={tc.id}
              onClick={() => navigate(`/test-cases/${tc.id}`)}
              className="glass-panel rounded-2xl border border-white/5 p-6 hover:border-primary/40 hover:shadow-[0_0_30px_rgba(255,107,0,0.1)] transition-all duration-300 group cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="absolute right-0 top-0 w-32 h-32 bg-primary/5 rounded-bl-[100px] -z-10 group-hover:bg-primary/10 transition-colors"></div>

              <div className="flex justify-between items-start mb-5 relative z-10">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black text-primary uppercase tracking-widest bg-primary/10 px-2 py-1 rounded-md border border-primary/20">{tc.tcCode}</span>
                    {tc.priority === 'High' && (
                      <span className="flex h-2 w-2 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-black text-white group-hover:text-primary transition-colors uppercase tracking-tight leading-tight max-w-4xl">{tc.title}</h3>
                  <p className="text-sm text-text-secondary leading-relaxed max-w-4xl line-clamp-2 font-medium">{tc.description}</p>
                </div>
                <div className="text-text-secondary/50 group-hover:text-primary transition-colors p-2 bg-white/5 hover:bg-primary/10 rounded-xl opacity-0 group-hover:opacity-100 border border-transparent group-hover:border-primary/20">
                  <EyeIcon className="h-5 w-5" />
                </div>
              </div>

              <div className="flex items-center gap-4 relative z-10 pt-4 border-t border-white/5">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-widest font-black ${
                  tc.priority === 'High' ? 'bg-red-500/10 text-red-400 border border-red-500/30' :
                  tc.priority === 'Medium' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/30' :
                  'bg-info/10 text-info border border-info/30'
                }`}>
                  <div className={`h-1.5 w-1.5 rounded-full ${
                    tc.priority === 'High' ? 'bg-red-500' :
                    tc.priority === 'Medium' ? 'bg-orange-500' :
                    'bg-info'
                  }`} />
                  {tc.priority}
                </div>
                <div className="bg-purple-500/10 text-purple-400 px-3 py-1.5 rounded-lg text-[10px] font-black border border-purple-500/30 uppercase tracking-widest">
                  {tc.type || 'Functional'}
                </div>
                <div className="flex items-center gap-1.5 text-text-secondary/70 text-[10px] font-black uppercase tracking-widest ml-auto">
                  <ClockIcon className="h-4 w-4 text-primary/70" />
                  {tc.steps?.length || 0} Sequences
                </div>
              </div>
            </div>
          ))}
          {filteredTestCases.length === 0 && (
            <div className="text-center py-24 glass-panel rounded-2xl border border-white/5">
              <div className="relative w-32 h-32 mx-auto mb-8">
                <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full"></div>
                <img src="/DocSprintLogo.png" alt="DocSprintAI" className="w-full h-full object-contain relative z-10 opacity-50 grayscale" />
              </div>
              <h3 className="text-white font-black text-xl mb-2 tracking-tight">NULL MATRIX</h3>
              <p className="text-text-secondary text-sm font-medium">Reconfigure parameters or initialize generation.</p>
            </div>
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-8 relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20 text-primary">
                  <SparklesIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-white tracking-tight">Recompute Matrix</h2>
                  <p className="text-[11px] font-black text-text-secondary uppercase tracking-widest mt-1">Configure Generation Vectors</p>
                </div>
              </div>
              
              <div className="space-y-8 mt-8">
                <div>
                  <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-3">{testCases.length > 0 ? 'Regeneration Thoughts / Feedback (Optional)' : 'Context Vectors (Optional)'}</label>
                  <textarea
                    className="w-full border border-white/10 bg-black/40 rounded-xl px-5 py-4 focus:ring-2 focus:ring-primary/50 focus:border-primary/50 min-h-[120px] resize-none text-sm text-white placeholder-text-secondary/30 shadow-inner font-medium block"
                    placeholder={testCases.length > 0 ? "Provide your thoughts, vision, or feedback on previous output..." : "Input specific edge cases, logic gates, or constraints..."}
                    value={extraContext}
                    onChange={(e) => setExtraContext(e.target.value)}
                  />
                </div>

                <div className="bg-white/5 p-5 rounded-xl border border-white/5">
                  <div className="flex justify-between items-center mb-5">
                    <label className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Target Test Case Count</label>
                    <span className="bg-primary/10 text-primary px-3 py-1 rounded-lg text-[12px] font-black border border-primary/30 shadow-inner">{testCaseCount}</span>
                  </div>
                  <div className="px-1">
                    <Slider
                      value={testCaseCount}
                      min={1}
                      max={15}
                      step={1}
                      onChange={setTestCaseCount}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-text-secondary/50 mt-3 px-1">
                    <span>1 Vector</span>
                    <span>15 Vectors</span>
                  </div>
                  
                  {!isPro && (
                    <div className="flex items-center gap-2 text-[10px] font-black text-orange-400 uppercase tracking-widest mt-6 bg-orange-500/10 p-3 rounded-lg border border-orange-500/30">
                      <SparklesIcon className="h-4 w-4" />
                      STARTER CLASS: MAX 15 VECTORS
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-6 bg-black/40 flex justify-end gap-3 border-t border-white/5 backdrop-blur-xl relative z-10">
              <Button variant="outline" onClick={() => setShowGenerateModal(false)} className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase h-11 px-6">Abort</Button>
              <Button className="bg-primary hover:bg-orange-500 text-white shadow-[0_0_20px_rgba(255,107,0,0.3)] rounded-xl font-black tracking-widest text-[10px] uppercase h-11 px-8 border-none" onClick={handleConfirmGenerate}>
                <SparklesIcon className="h-4 w-4 mr-2" />
                Initialize Flow
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Test Case Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="glass-panel rounded-2xl shadow-[0_0_50px_rgba(0,0,0,0.6)] border border-white/10 w-full max-w-2xl overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-white/5 bg-black/40 flex justify-between items-center relative z-10">
              <div className="flex items-center gap-4">
                <div className="p-2.5 bg-emerald-500/10 rounded-xl border border-emerald-500/20 text-emerald-400">
                  <PlusIcon className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white tracking-tight">Manual Test Case Entry</h2>
                </div>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-text-secondary hover:text-white p-2 hover:bg-white/10 rounded-xl transition-all"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-8 space-y-6 relative z-10">
              <div>
                <Input
                  label={<span className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Test Case ID / Title</span>}
                  required
                  value={newTestCase.title}
                  onChange={(e) => setNewTestCase({ ...newTestCase, title: e.target.value })}
                  placeholder="E.g., SEQ-01: Authenticate Active Directory..."
                  className="bg-black/40 border-white/10 text-white placeholder-text-secondary/30 focus:border-primary/50 shadow-inner"
                />
              </div>

              <div>
                <Textarea
                  label={<span className="text-[11px] font-black text-text-secondary uppercase tracking-widest">Functional Module</span>}
                  rows={3}
                  value={newTestCase.description}
                  onChange={(e) => setNewTestCase({ ...newTestCase, description: e.target.value })}
                  placeholder="Define execution parameters and expected state output..."
                  className="bg-black/40 border-white/10 text-white placeholder-text-secondary/30 focus:border-primary/50 shadow-inner"
                />
              </div>

              <div className="grid grid-cols-2 gap-6 bg-white/5 p-6 rounded-xl border border-white/5">
                <div>
                  <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-3">Priority Vector</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-black text-white shadow-inner"
                    value={newTestCase.priority}
                    onChange={(e) => setNewTestCase({ ...newTestCase, priority: e.target.value })}
                  >
                    <option value="High">High Priority</option>
                    <option value="Medium">Medium Priority</option>
                    <option value="Low">Low Priority</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-black text-text-secondary uppercase tracking-widest mb-3">Class Vector</label>
                  <select
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all font-black text-white shadow-inner"
                    value={newTestCase.type}
                    onChange={(e) => setNewTestCase({ ...newTestCase, type: e.target.value })}
                  >
                    <option value="Functional">Functional</option>
                    <option value="Non-Functional">Non-Functional</option>
                    <option value="UI">UI / Layout</option>
                    <option value="Security">Security / Auth</option>
                    <option value="Performance">Performance / Load</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 bg-black/40 flex justify-end gap-3 border-t border-white/5 backdrop-blur-xl relative z-10">
              <Button
                variant="outline"
                className="rounded-xl border-white/10 bg-white/5 text-white hover:bg-white/10 font-black tracking-widest text-[10px] uppercase h-11 px-6"
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancel Injection
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 shadow-[0_0_20px_rgba(16,185,129,0.3)] font-black tracking-widest text-[10px] uppercase h-11 border-none"
                onClick={handleCreateTestCase}
                disabled={creatingManual || !newTestCase.title}
              >
                {creatingManual ? 'Compiling...' : 'Save Test Case'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
